import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang, t } from "@/context/LangContext";
import Ticker from "@/components/Ticker";
import { ArrowUpRight, Calculator, BookOpen, LineChart, Bot, Rocket, Sparkles } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/38412413/pexels-photo-38412413.jpeg";
const EDU_IMG = "https://images.pexels.com/photos/7578799/pexels-photo-7578799.jpeg";
const CHART_IMG = "https://images.pexels.com/photos/6203470/pexels-photo-6203470.jpeg";

const HomePage = () => {
  const { lang } = useLang();
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="stock market monitors" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline text-white/50 mb-6">{t(lang, "Platform Edukasi & Analisa", "Education & Analysis Platform")}</div>
            <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter max-w-4xl">
              {t(lang, "Investasi cerdas.", "Invest smarter.")}
              <br />
              <span className="text-white/40">{t(lang, "Analisa mendalam.", "Analyze deeper.")}</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-white/70 leading-relaxed">
              {t(lang,
                "Pelajari saham IDX dan bursa AS. Hitung nilai wajar dengan 5 metode valuasi. Tanya AI Analyst kapan saja.",
                "Master IDX and US stocks. Calculate fair value with 5 valuation methods. Ask our AI Analyst anytime.")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/analysis"
                data-testid="hero-cta-analysis"
                className="group inline-flex items-center gap-2 bg-white text-black font-medium px-6 py-3 rounded-full hover:bg-white/90 transition-colors"
              >
                {t(lang, "Analisa Saham", "Analyze Stocks")}
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link
                to="/calculators"
                data-testid="hero-cta-calc"
                className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-full hover:bg-white/5 transition-colors"
              >
                {t(lang, "Coba Kalkulator", "Try Calculators")}
              </Link>
            </div>
          </motion.div>
        </div>
        <Ticker />
      </section>

      {/* Bento Feature Grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline text-white/40 mb-3">{t(lang, "Yang Kami Tawarkan", "What We Offer")}</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight max-w-xl">
              {t(lang, "Semua yang Anda butuhkan, dalam satu tempat.", "Everything you need, in one place.")}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 lg:gap-6">
          {/* Big card */}
          <Link to="/analysis" data-testid="card-analysis" className="md:col-span-4 md:row-span-2 group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d0d] p-8 trace-card min-h-[320px] flex flex-col justify-between">
            <div>
              <LineChart size={36} strokeWidth={1.4} className="mb-6 text-white/80" />
              <div className="overline text-white/40 mb-2">01 · {t(lang, "Analisa", "Analysis")}</div>
              <h3 className="font-display font-bold text-3xl leading-tight tracking-tight mb-3">
                {t(lang, "Analisa saham IDX & US", "IDX & US stock analysis")}
              </h3>
              <p className="text-white/60 max-w-md">
                {t(lang, "Fundamental lengkap: PER, PBV, ROE, dan histori harga 30 hari.", "Complete fundamentals: P/E, P/B, ROE, and 30-day price history.")}
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/60 group-hover:text-white transition-colors">
              {t(lang, "Jelajahi Database", "Explore Database")} <ArrowUpRight size={16} />
            </div>
          </Link>

          <Link to="/calculators" data-testid="card-calc" className="md:col-span-2 group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 trace-card min-h-[150px]">
            <Calculator size={28} strokeWidth={1.4} className="mb-4 text-white/80" />
            <div className="overline text-white/40 mb-1">02</div>
            <h3 className="font-display font-bold text-xl tracking-tight">
              {t(lang, "5 Metode Valuasi", "5 Valuation Methods")}
            </h3>
            <p className="text-sm text-white/50 mt-2">DCF · PER · PBV · Graham · DDM</p>
          </Link>

          <Link to="/ipo" data-testid="card-ipo" className="md:col-span-2 group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 trace-card min-h-[150px]">
            <Rocket size={28} strokeWidth={1.4} className="mb-4 text-white/80" />
            <div className="overline text-white/40 mb-1">03</div>
            <h3 className="font-display font-bold text-xl tracking-tight">{t(lang, "Kalender IPO", "IPO Calendar")}</h3>
            <p className="text-sm text-white/50 mt-2">{t(lang, "Indonesia & Amerika", "Indonesia & America")}</p>
          </Link>

          <Link to="/recommendations" data-testid="card-recs" className="md:col-span-3 group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 trace-card min-h-[200px] flex flex-col justify-between">
            <div>
              <Sparkles size={28} strokeWidth={1.4} className="mb-4 text-white/80" />
              <div className="overline text-white/40 mb-1">04</div>
              <h3 className="font-display font-bold text-2xl tracking-tight">
                {t(lang, "Rekomendasi Pilihan", "Curated Picks")}
              </h3>
              <p className="text-sm text-white/50 mt-2 max-w-sm">
                {t(lang, "Thesis, target price, dan risiko untuk saham ID & US.", "Thesis, target price, and risks for ID & US stocks.")}
              </p>
            </div>
          </Link>

          <Link to="/ai-chat" data-testid="card-ai" className="md:col-span-3 group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 trace-card min-h-[200px] flex flex-col justify-between">
            <div className="absolute inset-0 opacity-20 -z-0">
              <img src={CHART_IMG} alt="chart" className="w-full h-full object-cover" />
            </div>
            <div className="relative">
              <Bot size={28} strokeWidth={1.4} className="mb-4 text-white/80" />
              <div className="overline text-white/40 mb-1">05</div>
              <h3 className="font-display font-bold text-2xl tracking-tight">
                {t(lang, "AI Analyst 24/7", "AI Analyst 24/7")}
              </h3>
              <p className="text-sm text-white/50 mt-2 max-w-sm">
                {t(lang, "Powered by Claude Sonnet 4.5", "Powered by Claude Sonnet 4.5")}
              </p>
            </div>
          </Link>

          <Link to="/education" data-testid="card-edu" className="md:col-span-6 group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d0d] p-8 min-h-[200px] grid md:grid-cols-2 gap-6 items-center">
            <div>
              <BookOpen size={28} strokeWidth={1.4} className="mb-4 text-white/80" />
              <div className="overline text-white/40 mb-1">06 · {t(lang, "Perpustakaan", "Library")}</div>
              <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-3">
                {t(lang, "Belajar dari nol sampai mahir", "Learn from zero to expert")}
              </h3>
              <p className="text-white/60">
                {t(lang, "Artikel bertingkat: Pemula, Menengah, Lanjutan. Dwibahasa ID/EN.", "Tiered articles: Beginner, Intermediate, Advanced. Bilingual ID/EN.")}
              </p>
            </div>
            <div className="relative h-32 md:h-full min-h-[150px] rounded-xl overflow-hidden">
              <img src={EDU_IMG} alt="learn" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/70 to-transparent" />
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
